import { TypedDataEncoder, getAddress, keccak256, verifyTypedData } from "ethers";

export const SESSION_DURATION_SECONDS = 8 * 60 * 60;
export const SESSION_MAX_CLAIMS = 20;

export const SESSION_GRANT_TYPES = Object.freeze({
  SessionGrant: Object.freeze([
    Object.freeze({ name: "worker", type: "address" }),
    Object.freeze({ name: "sessionKey", type: "address" }),
    Object.freeze({ name: "validAfter", type: "uint64" }),
    Object.freeze({ name: "validUntil", type: "uint64" }),
    Object.freeze({ name: "maxClaims", type: "uint32" }),
    Object.freeze({ name: "epoch", type: "uint256" })
  ])
});

export const SESSION_CLAIM_TYPES = Object.freeze({
  SessionClaim: Object.freeze([
    Object.freeze({ name: "grantDigest", type: "bytes32" }),
    Object.freeze({ name: "jobId", type: "bytes32" }),
    Object.freeze({ name: "nonce", type: "uint256" }),
    Object.freeze({ name: "authorizationDeadline", type: "uint64" }),
    Object.freeze({ name: "authorizationHash", type: "bytes32" })
  ])
});

export const SESSION_SUBMIT_TYPES = Object.freeze({
  SessionSubmit: Object.freeze([
    Object.freeze({ name: "grantDigest", type: "bytes32" }),
    Object.freeze({ name: "jobId", type: "bytes32" }),
    Object.freeze({ name: "outputHash", type: "bytes32" }),
    Object.freeze({ name: "nonce", type: "uint256" })
  ])
});

export function sessionDomain(contractAddress, chainId = 46630) {
  return {
    name: "ShareMyAI TokenWorkEscrow",
    version: "1",
    chainId: Number(chainId),
    verifyingContract: getAddress(contractAddress)
  };
}

export function createSessionGrant({ worker, sessionKey, epoch, nowSeconds = Math.floor(Date.now() / 1000) }) {
  return normalizeSessionGrant({
    worker,
    sessionKey,
    validAfter: Math.max(0, nowSeconds - 30),
    validUntil: nowSeconds + SESSION_DURATION_SECONDS,
    maxClaims: SESSION_MAX_CLAIMS,
    epoch
  });
}

export function normalizeSessionGrant(grant) {
  const normalized = {
    worker: getAddress(grant?.worker),
    sessionKey: getAddress(grant?.sessionKey),
    validAfter: boundedInteger(grant?.validAfter, 0, Number.MAX_SAFE_INTEGER, "validAfter"),
    validUntil: boundedInteger(grant?.validUntil, 1, Number.MAX_SAFE_INTEGER, "validUntil"),
    maxClaims: boundedInteger(grant?.maxClaims, 1, 25, "maxClaims"),
    epoch: String(BigInt(grant?.epoch ?? 0))
  };
  if (normalized.validUntil <= normalized.validAfter) throw new Error("Session expiry must follow its start time.");
  if (normalized.validUntil - normalized.validAfter > 12 * 60 * 60) {
    throw new Error("Session duration exceeds the contract's 12-hour safety limit.");
  }
  return normalized;
}

export function sessionGrantTypedData(contractAddress, grant, chainId = 46630) {
  const message = normalizeSessionGrant(grant);
  return {
    domain: sessionDomain(contractAddress, chainId),
    types: SESSION_GRANT_TYPES,
    primaryType: "SessionGrant",
    message
  };
}

export function sessionGrantDigest(contractAddress, grant, chainId = 46630) {
  const typedData = sessionGrantTypedData(contractAddress, grant, chainId);
  return TypedDataEncoder.hash(typedData.domain, typedData.types, typedData.message);
}

export function sessionClaimTypedData(contractAddress, input, chainId = 46630) {
  const message = {
    grantDigest: normalizeBytes32(input.grantDigest, "grantDigest"),
    jobId: normalizeBytes32(input.jobId, "jobId"),
    nonce: String(BigInt(input.nonce)),
    authorizationDeadline: boundedInteger(
      input.authorizationDeadline,
      1,
      Number.MAX_SAFE_INTEGER,
      "authorizationDeadline"
    ),
    authorizationHash: normalizeBytes32(input.authorizationHash, "authorizationHash")
  };
  return {
    domain: sessionDomain(contractAddress, chainId),
    types: SESSION_CLAIM_TYPES,
    primaryType: "SessionClaim",
    message
  };
}

export function sessionSubmitTypedData(contractAddress, input, chainId = 46630) {
  const message = {
    grantDigest: normalizeBytes32(input.grantDigest, "grantDigest"),
    jobId: normalizeBytes32(input.jobId, "jobId"),
    outputHash: normalizeBytes32(input.outputHash, "outputHash"),
    nonce: String(BigInt(input.nonce))
  };
  return {
    domain: sessionDomain(contractAddress, chainId),
    types: SESSION_SUBMIT_TYPES,
    primaryType: "SessionSubmit",
    message
  };
}

export function recoverSessionGrantWorker(contractAddress, grant, signature, chainId = 46630) {
  const typedData = sessionGrantTypedData(contractAddress, grant, chainId);
  return getAddress(verifyTypedData(typedData.domain, typedData.types, typedData.message, signature));
}

export function recoverSessionActionSigner(typedData, signature) {
  return getAddress(verifyTypedData(typedData.domain, typedData.types, typedData.message, signature));
}

export function authorizationHash(signature) {
  if (!/^0x[0-9a-fA-F]{130}$/.test(String(signature || ""))) throw new Error("Claim authorization is invalid.");
  return keccak256(signature);
}

function normalizeBytes32(value, label) {
  const normalized = String(value || "").toLowerCase();
  if (!/^0x[0-9a-f]{64}$/.test(normalized)) throw new Error(`${label} must be a 32-byte hex value.`);
  return normalized;
}

function boundedInteger(value, minimum, maximum, label) {
  const parsed = Number(value);
  if (!Number.isSafeInteger(parsed) || parsed < minimum || parsed > maximum) {
    throw new Error(`${label} is outside the supported range.`);
  }
  return parsed;
}
